import { Controller } from "@hotwired/stimulus";
import { priceBlockHtml, bestVariant } from "../flash_price";
import { formatTND } from "../lib/currency"

export default class extends Controller {
  static targets = ["brandName", "brandImage", "brandSlogan", "perfumesGrid", "pagination"];
  static values = { brandId: Number };

  allPerfumes = [];
  currentPage = 1;
  perfumesPerPage = 12;

  connect() {
    this.fetchBrandPerfumes();
  }

  async fetchBrandPerfumes() {
    const apiUrl = `/api/v1/brands/${this.brandIdValue}`;

    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      this.renderBrandDetails(data);
      this.allPerfumes = data.parfums || [];

      const totalPages = Math.max(1, Math.ceil(this.allPerfumes.length / this.perfumesPerPage));
      this.currentPage = this.restoreSavedPage(totalPages);

      this.renderPerfumes();
      this.renderPagination();
    } catch (error) {
      console.error("Erreur lors de la récupération des parfums de la marque:", error);
      this.perfumesGridTarget.innerHTML = "<p class='error-message'>Impossible de charger les parfums. Veuillez réessayer plus tard.</p>";
      this.brandNameTarget.textContent = "Marque Inconnue";
      this.brandImageTarget.src = "/placeholder.svg?height=200&width=200";
      this.brandSloganTarget.textContent = "Une erreur est survenue lors du chargement.";
    }
  }

  renderBrandDetails(brand) {
    this.brandNameTarget.textContent = brand.name || "Marque Inconnue";
    this.brandImageTarget.src = brand.image_url || "/placeholder.svg?height=200&width=200";
    this.brandImageTarget.alt = `${brand.name} Logo`;
    this.brandSloganTarget.textContent = `Découvrez l'essence de ${brand.name} à travers nos décants exclusifs.`;
  }

  renderPerfumes() {
    const startIndex = (this.currentPage - 1) * this.perfumesPerPage;
    const endIndex = startIndex + this.perfumesPerPage;
    const perfumesToDisplay = this.allPerfumes.slice(startIndex, endIndex);

    if (perfumesToDisplay.length === 0) {
      this.perfumesGridTarget.innerHTML = "<p class='no-perfumes-message'>Aucun parfum trouvé pour cette marque pour le moment.</p>";
      return;
    }

    const perfumeHtml = perfumesToDisplay.map(perfume => {
      const imageUrl = perfume.image_url || "/placeholder.svg?height=250&width=250&text=Parfum";
      const availabilityStatus = perfume.disponible ? 'Disponible' : 'Rupture de stock';
      const availabilityClass = perfume.disponible ? 'perfume-availability-badge--available' : 'perfume-availability-badge--unavailable';

      const bestV = bestVariant(perfume);
      const defaultSize = bestV ? bestV.size : '10ml';
      const defaultPrice = bestV ? (bestV.promo_price ?? bestV.price) : this.getMinPrice(perfume);
      const defaultMaxQuantity = (bestV && bestV.promo_price != null) ? (bestV.promo_remaining ?? "") : "";

      return `
        <div class="perfume-card">
          <a href="/vitrine/parfums/${perfume.slug || perfume.id}" class="perfume-card-link">
            <div class="perfume-image-wrapper">
              <img src="${imageUrl}" alt="${perfume.name}" class="perfume-image">
              <div class="image-overlay"></div>
            </div>
            <div class="perfume-content">
              <div class="perfume-card-header-row">
                <h3 class="perfume-name">${perfume.name}</h3>
                <span class="perfume-availability-badge ${availabilityClass}">${availabilityStatus}</span>
              </div>
              <div class="perfume-meta">
                <span class="perfume-category">${perfume.category ? perfume.category.charAt(0).toUpperCase() + perfume.category.slice(1) : ''}</span>
                <span class="perfume-class">${perfume.fragrance_class ? perfume.fragrance_class.charAt(0).toUpperCase() + perfume.fragrance_class.slice(1) : ''}</span>
              </div>
              <div class="perfume-price-row">
                ${priceBlockHtml(perfume)}
                <span class="perfume-volume">${defaultSize}</span>
              </div>
              <div class="perfume-card-actions">
                <button
                  class="perfume-card-add-to-cart-btn"
                  data-action="click->cart#addToCart"
                  data-parfum-id="${perfume.id}"
                  data-parfum-name="${perfume.name}"
                  data-parfum-price="${defaultPrice}"
                  data-parfum-max-quantity="${defaultMaxQuantity}"
                  data-parfum-size="${defaultSize}"
                  data-parfum-image-url="${imageUrl}"
                  ${!perfume.disponible ? 'disabled' : ''}
                >
                  Ajouter au panier
                </button>
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');

    this.perfumesGridTarget.innerHTML = perfumeHtml;
  }

  renderPagination() {
    const totalPages = Math.ceil(this.allPerfumes.length / this.perfumesPerPage);
    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination">';

    paginationHtml += `
      <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
        <a href="#" data-action="click->brand-perfumes#previousPage" class="page-link">Précédent</a>
      </li>
    `;

    for (const page of this.getPageNumbers(this.currentPage, totalPages)) {
      if (page === '...') {
        paginationHtml += `
          <li class="page-item disabled page-item--ellipsis">
            <span class="page-link">&hellip;</span>
          </li>
        `;
      } else {
        paginationHtml += `
          <li class="page-item ${this.currentPage === page ? 'current' : ''}">
            <a href="#" data-action="click->brand-perfumes#goToPage" data-page="${page}" class="page-link">${page}</a>
          </li>
        `;
      }
    }

    paginationHtml += `
      <li class="page-item ${this.currentPage === totalPages ? 'disabled' : ''}">
        <a href="#" data-action="click->brand-perfumes#nextPage" class="page-link">Suivant</a>
      </li>
    `;

    paginationHtml += '</ul>';
    this.paginationTarget.innerHTML = paginationHtml;
  }

  previousPage(event) {
    event.preventDefault();
    if (this.currentPage > 1) {
      this.currentPage--;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(event) {
    event.preventDefault();
    const totalPages = Math.ceil(this.allPerfumes.length / this.perfumesPerPage);
    if (this.currentPage < totalPages) {
      this.currentPage++;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  goToPage(event) {
    event.preventDefault();
    const page = parseInt(event.currentTarget.dataset.page);
    if (page && page !== this.currentPage) {
      this.currentPage = page;
      this.savePage();
      this.renderPerfumes();
      this.renderPagination();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Construit la liste des numéros de page à afficher, avec des "..." pour les écarts
  getPageNumbers(current, total) {
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - 1 && i <= current + 1)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== '...') {
        pages.push('...');
      }
    }
    return pages;
  }

  getPageStorageKey() {
    return `pagination:${window.location.pathname}${window.location.search}`;
  }

  restoreSavedPage(totalPages) {
    try {
      const saved = parseInt(sessionStorage.getItem(this.getPageStorageKey()), 10);
      if (saved >= 1 && saved <= totalPages) {
        return saved;
      }
    } catch (e) {}
    return 1;
  }

  savePage() {
    try {
      sessionStorage.setItem(this.getPageStorageKey(), String(this.currentPage));
    } catch (e) {}
  }

  // NOUVELLE MÉTHODE: Obtenir le prix minimum
  getMinPrice(perfume) {
    if (!perfume.variants || perfume.variants.length === 0) {
      return 0;
    }
    return Math.min(...perfume.variants.map(v => parseFloat(v.price)));
  }

  formatCurrency(amount) {
    return formatTND(amount)
  }

  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
}